import React, { FC, JSX } from 'react';
import { Col, Container, Row, Tab } from 'react-bootstrap';
import styles from './AccessoriesTab.module.scss';
import { IAccessoriesTabProps } from './types/IAccessoriesTabProps';
import { useData } from '../../../../../helpers/hooks/useData';
import { useFormat } from '../../../../../helpers/hooks/useFormat';
import { TNikonProduct, TNikonVariant } from '../../../../../types/TNikonProduct';
import ProductItem from '../../../atoms/ProductItem';
import { ImageHelpers } from 'helpers/imageHelpers';
import { IMAGE_SOURCE } from 'helpers/constants/imageSources';
import { useRouter } from 'next/router';

const NotForSaleProduct: FC<any> = ({ variant, name, message }) => {
  const { locale: routerLocale } = useRouter();
  const imageUnavailable = IMAGE_SOURCE.getUnavailableImage(routerLocale);
  const image = variant?.representativeImage || variant?.images?.[0] || imageUnavailable;
  return (
    <Col xl={2} md={3} as="li" className="">
      <div data-testid="NotForSaleProduct" className={styles['container']}>
        <div className={styles['product-image']}>
          <div className={styles['image-container']}>
            <img
              data-testid="NotForSaleProductImage"
              src={ImageHelpers.modifiedCTImageUrl(image, 'small')}
              loading="lazy"
              className={styles['img']}
              onError={({ currentTarget }) => {
                currentTarget.src = imageUnavailable;
              }}
              alt={name}
            />
          </div>
        </div>
        <div className={styles['content']}>
          <div className={styles['description']}>
            <p className={styles['title']}>
              <span data-testid="NotForSaleProductTitle">{name}</span>
            </p>
            <i>{message}</i>
          </div>
        </div>
      </div>
    </Col>
  );
};

const AccessoriesTab = ({ product }: IAccessoriesTabProps) => {
  const { formatMessage } = useFormat({ name: 'product' });

  const [relatedAccessoriesProducts, isValidAccessoriesProducts] = useData<TNikonVariant, TNikonProduct[]>(
    product.variants[0],
    {
      return: 'attributes.relatedAccessories',
    },
  );
  const [suppliedAccessoriesProducts, isValidSuppliedProducts] = useData<TNikonVariant, TNikonProduct[]>(
    product.variants[0],
    {
      return: 'attributes.suppliedProducts',
    },
  );

  if (!isValidSuppliedProducts && !isValidAccessoriesProducts) {
    return false as unknown as JSX.Element;
  }

  const notForResaleMessage = formatMessage({
    id: 'accessoriesTab.noResale',
    defaultMessage: 'Not available for individual sale.',
  });

  return (
    <Tab.Pane data-testid="container" eventKey="accessories" className="productsTabPane">
      <h2 data-testid="title">{formatMessage({ id: 'accessoriesTab.title', defaultMessage: 'Accessories' })}</h2>
      {isValidSuppliedProducts && (
        <Container className={`pb-[60px] ${styles.accessoriesContainer}`}>
          <h3 data-testid="supplied-title">
            {formatMessage({ id: 'accessoriesTab.suppliedTitle', defaultMessage: 'Supplied Accessories' })}
          </h3>
          <Row className={styles['row']} as="ul">
            {suppliedAccessoriesProducts.map((item, idx) => {
              const variant = item?.variants?.[0];

              if (!variant?.attributes?.isAvailableForSale) {
                return <NotForSaleProduct key={idx} variant={variant} name={item.name} message={notForResaleMessage} />;
              }
              return <ProductItem key={idx} product={item} />;
            })}
          </Row>
        </Container>
      )}
      {isValidAccessoriesProducts && (
        <Container className={styles.accessoriesContainer}>
          <h3 data-testid="related-title">
            {formatMessage({ id: 'accessoriesTab.relatedTitle', defaultMessage: 'Related Accessories' })}
          </h3>
          <Row className={styles['row']} as="ul">
            {relatedAccessoriesProducts.map((item, index) => (
              <ProductItem key={`accessories-item-${item.productId || index}`} product={item} />
            ))}
          </Row>
        </Container>
      )}
    </Tab.Pane>
  );
};

export default AccessoriesTab;
